
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { LegacyInvoicesFilters } from "@/data/filters/invoice";
import { LegacyInvoicesSorters } from "@/data/sorters/invoices";

// Typings
import {
  ILegacyInvoicesProviderData,
  ILegacyInvoicesProviderMethods
} from "@/models/providers/legacyInvoices";
import { IClient } from "@/models/clients";
import { Sorters } from "@/data/table";

@Component
export default class LegacyInvoicesProvider extends Mixins(
  FilteringSortingTableStore
) {
  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: "IIP" }) // [I]mported [I]nvoice [P]rovider
  public declare id: string;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, string | number | boolean>;

  @Prop({ default: null })
  public clientId!: IClient["id"] | null;

  // Data
  public dataModule: DataModules = DataModules.LEGACY_INVOICES;
  public filters = LegacyInvoicesFilters();
  public sorters = LegacyInvoicesSorters();

  // Computed

  public get clientFilter() {
    if (!this.clientId) return {};
    return { "filter[client_id]": this.clientId };
  }

  public get stagedImportFilter() {
    return this.clientId ? { with_staged_imports: 1 } : {};
  }

  public get contentIsAvailable() {
    return this.$userCan(...["list_import_invoices"]);
  }

  // Methods

  public reloadData() {
    this.getData({ ...this.config, ignoreStored: true });
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          ...this.clientFilter,
          ...this.stagedImportFilter,
          ...this.manualFilters
        }
      },
      params
    );
  }

  // Life-cycle Methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.defaults.sort = {
      field: "create_datetime",
      direction: Sorters.DESCENDING
    };
    this.getData();
  }

  public render(h: CreateElement) {
    // Guarding
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });
    // Handle loading
    if (this?.handleLoading && this.table.loading)
      return h("is-loading", { props: { if: true }, attrs: this?.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $legacyInvoicesData: this.providerData(),
        $legacyInvoicesMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("legacyInvoicesProviderData")
  public providerData(): ILegacyInvoicesProviderData {
    return {
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      enablePagination: this.enablePagination,
      activeFilters: this.activeFilters,
      filters: this.filters,
      sorters: this.sorters,
      table: this.table
    };
  }

  @Provide("legacyInvoicesProviderMethods")
  public get providerMethods(): ILegacyInvoicesProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort
    };
  }
}
